import { useState } from "react";
import { useQuery } from "react-query";
import { api } from "@/lib/api";
import { DEFAULT_REPORT_FILTERS } from "@/lib/reportDefaults";
import { Card, CardContent } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { ReportFilters } from "@/components/reports/ReportFilters";
import { ReportExport } from "@/components/reports/ReportExport";
import { formatCurrency, cn } from "@/lib/utils";
import {
  Calculator,
  CheckCircle2,
  Clock,
  AlertTriangle,
  RefreshCw,
  BarChart3,
  PiggyBank,
  CreditCard,
  AlertOctagon,
  Wallet,
  Send,
  FileCheck,
  TrendingUp,
} from "lucide-react";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from "recharts";

interface PayrollData {
  summary: {
    savingsDeductions: number;
    loanRepayments: number;
    penaltyDeductions: number;
    totalDeductions: number;
    hrReady: number;
    sentToHr: number;
    processed: number;
  };
  monthlyTrends: Array<{
    month: string;
    savings: number;
    loans: number;
    penalties: number;
  }>;
  memberDeductions: Array<{
    memberId: string;
    memberName: string;
    savings: number;
    loanRepayments: number;
    penalties: number;
    total: number;
    hrStatus: string;
  }>;
  hrReadyList: Array<{
    memberId: string;
    memberName: string;
    total: number;
    breakdown: string;
  }>;
}

function SummaryCard({
  title,
  value,
  icon,
  accentColor,
  loading = false,
}: {
  title: string;
  value: string | number;
  icon: React.ReactNode;
  accentColor: string;
  loading?: boolean;
}) {
  const bgMap: Record<string, string> = {
    green: "bg-[#009A44]/10",
    red: "bg-red-50",
    blue: "bg-blue-50",
    amber: "bg-amber-50",
    purple: "bg-purple-50",
    gray: "bg-gray-100",
  };
  const colorMap: Record<string, string> = {
    green: "text-[#009A44]",
    red: "text-red-600",
    blue: "text-blue-600",
    amber: "text-amber-600",
    purple: "text-purple-600",
    gray: "text-gray-600",
  };

  return (
    <Card>
      <CardContent className="p-4">
        <div className="flex items-center justify-between mb-2">
          <span className="text-xs font-medium text-gray-600">{title}</span>
          <div
            className={cn(
              "flex h-9 w-9 items-center justify-center rounded-lg",
              bgMap[accentColor] || "bg-gray-50"
            )}
          >
            <div className={cn("h-4 w-4", colorMap[accentColor] || "text-gray-600")}>
              {icon}
            </div>
          </div>
        </div>
        {loading ? (
          <Skeleton className="h-7 w-24" />
        ) : (
          <p className={cn("text-xl font-bold", colorMap[accentColor] || "text-gray-900")}>
            {typeof value === "number" ? formatCurrency(value) : value}
          </p>
        )}
      </CardContent>
    </Card>
  );
}

const hrStatusBadge: Record<string, string> = {
  ready: "bg-[#009A44]/10 text-[#009A44]",
  sent: "bg-blue-50 text-blue-600",
  processed: "bg-gray-100 text-gray-600",
  pending: "bg-amber-50 text-amber-600",
};

export default function PayrollReport() {
  const [filters, setFilters] = useState<Record<string, any>>(DEFAULT_REPORT_FILTERS);

  const {
    data: rawData,
    status,
    error,
    refetch,
  } = useQuery(
    ["reports-payroll", filters],
    async () => {
      const params: Record<string, string> = {};
      if (filters.dateStart) params.dateStart = filters.dateStart;
      if (filters.dateEnd) params.dateEnd = filters.dateEnd;
      if (filters.month) params.month = filters.month;
      if (filters.year) params.year = filters.year;

      const { data } = await api.get("/reports/payroll-deductions", { params });
      return data?.data as PayrollData;
    },
    {
      staleTime: 30000,
      cacheTime: 60000,
      retry: 1,
      refetchOnWindowFocus: false,
    }
  );

  const loading = status === "loading";
  const summary = rawData?.summary || {
    savingsDeductions: 0,
    loanRepayments: 0,
    penaltyDeductions: 0,
    totalDeductions: 0,
    hrReady: 0,
    sentToHr: 0,
    processed: 0,
  };
  const monthlyTrends = rawData?.monthlyTrends || [];
  const memberDeductions = rawData?.memberDeductions || [];
  const hrReadyList = rawData?.hrReadyList || [];

  const exportColumns = [
    { key: "memberName", label: "Member" },
    { key: "savings", label: "Savings" },
    { key: "loanRepayments", label: "Loan Repayments" },
    { key: "penalties", label: "Penalties" },
    { key: "total", label: "Total" },
    { key: "hrStatus", label: "HR Status" },
  ];

  const hrReadyColumns = [
    { key: "memberName", label: "Member" },
    { key: "total", label: "Total Deduction" },
    { key: "breakdown", label: "Breakdown" },
  ];

  if (status === "error") {
    return (
      <div className="min-h-screen w-full max-w-full overflow-x-hidden">
        <div className="space-y-5 min-w-0">
          <Card className="border-none shadow-sm bg-gradient-to-r from-[#009A44] to-[#007a35] overflow-hidden">
            <div className="p-4 sm:p-6">
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                <div>
                  <h1 className="text-lg sm:text-xl lg:text-2xl font-bold text-white">
                    Payroll Report
                  </h1>
                  <p className="mt-1.5 text-sm sm:text-base text-white/80">
                    Payroll deduction tracking and HR processing status
                  </p>
                </div>
              </div>
            </div>
          </Card>
          <div className="flex min-h-[50vh] items-center justify-center p-6">
            <div className="text-center">
              <AlertTriangle className="mx-auto mb-4 h-12 w-12 text-red-400" />
              <h2 className="mb-2 text-lg font-semibold text-gray-900">
                Failed to load payroll report
              </h2>
              <p className="mb-4 text-sm text-gray-500">
                {(error as any)?.response?.data?.message ||
                  (error as any)?.message ||
                  "Could not connect to the server."}
              </p>
              <button
                onClick={() => refetch()}
                className="inline-flex items-center gap-2 rounded-lg border border-red-200 bg-white px-4 py-2 text-sm font-medium text-red-600 hover:bg-red-50"
              >
                <RefreshCw className="h-4 w-4" /> Retry
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen w-full max-w-full overflow-x-hidden">
      <div className="space-y-5 min-w-0">
        {/* Header */}
        <Card className="border-none shadow-sm bg-gradient-to-r from-[#009A44] to-[#007a35] overflow-hidden">
          <div className="p-4 sm:p-6">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
              <div className="min-w-0">
                <div className="flex items-center gap-3">
                  <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-white/20">
                    <Calculator className="h-5 w-5 text-white" />
                  </div>
                  <div>
                    <h1 className="text-lg sm:text-xl lg:text-2xl font-bold text-white">
                      Payroll Report
                    </h1>
                    <p className="mt-0.5 text-sm sm:text-base text-white/80">
                      Payroll deduction tracking and HR processing status
                    </p>
                  </div>
                </div>
              </div>
              <button
                onClick={() => refetch()}
                className="inline-flex items-center gap-2 rounded-lg border border-white/20 bg-white/10 px-3 py-2 text-sm font-medium text-white hover:bg-white/20 shrink-0"
              >
                <RefreshCw className="h-4 w-4" />
                Refresh
              </button>
            </div>
          </div>
        </Card>

        {/* Filters */}
        <ReportFilters
          filters={filters}
          onChange={setFilters}
          show={["dateStart", "dateEnd", "month", "year"]}
        />

        {/* Summary Cards - Row 1 */}
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          <SummaryCard
            title="Savings Deductions"
            value={summary.savingsDeductions}
            icon={<PiggyBank className="h-5 w-5" />}
            accentColor="green"
            loading={loading}
          />
          <SummaryCard
            title="Loan Repayments"
            value={summary.loanRepayments}
            icon={<CreditCard className="h-5 w-5" />}
            accentColor="blue"
            loading={loading}
          />
          <SummaryCard
            title="Penalty Deductions"
            value={summary.penaltyDeductions}
            icon={<AlertOctagon className="h-5 w-5" />}
            accentColor="red"
            loading={loading}
          />
          <SummaryCard
            title="Total Deductions"
            value={summary.totalDeductions}
            icon={<Wallet className="h-5 w-5" />}
            accentColor="amber"
            loading={loading}
          />
        </div>

        {/* Summary Cards - Row 2 */}
        <div className="grid gap-4 sm:grid-cols-3">
          <SummaryCard
            title="HR-Ready"
            value={summary.hrReady}
            icon={<FileCheck className="h-5 w-5" />}
            accentColor="green"
            loading={loading}
          />
          <SummaryCard
            title="Sent to HR"
            value={summary.sentToHr}
            icon={<Send className="h-5 w-5" />}
            accentColor="blue"
            loading={loading}
          />
          <SummaryCard
            title="Processed"
            value={summary.processed}
            icon={<CheckCircle2 className="h-5 w-5" />}
            accentColor="green"
            loading={loading}
          />
        </div>

        {/* Monthly Deduction Trends Chart */}
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-2 mb-4">
              <div className="flex h-7 w-7 items-center justify-center rounded-md bg-[#009A44]/10">
                <TrendingUp className="h-3.5 w-3.5 text-[#009A44]" />
              </div>
              <h3 className="text-sm font-semibold">Monthly Deduction Trends</h3>
            </div>
            {loading ? (
              <Skeleton className="h-64 w-full" />
            ) : monthlyTrends.length === 0 ? (
              <div className="py-12 text-center">
                <TrendingUp className="mx-auto mb-3 h-10 w-10 text-gray-300" />
                <p className="text-sm text-gray-500">No monthly trend data available</p>
              </div>
            ) : (
              <div className="h-64">
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={monthlyTrends}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                    <XAxis dataKey="month" tick={{ fontSize: 12 }} />
                    <YAxis tick={{ fontSize: 12 }} />
                    <Tooltip
                      formatter={(value: number) => formatCurrency(value)}
                      contentStyle={{
                        borderRadius: "8px",
                        border: "1px solid #e5e7eb",
                        boxShadow: "0 4px 6px -1px rgba(0,0,0,0.1)",
                      }}
                    />
                    <Legend />
                    <Line
                      type="monotone"
                      dataKey="savings"
                      name="Savings"
                      stroke="#009A44"
                      strokeWidth={2}
                      dot={{ r: 4 }}
                      activeDot={{ r: 6 }}
                    />
                    <Line
                      type="monotone"
                      dataKey="loans"
                      name="Loans"
                      stroke="#2196F3"
                      strokeWidth={2}
                      dot={{ r: 4 }}
                      activeDot={{ r: 6 }}
                    />
                    <Line
                      type="monotone"
                      dataKey="penalties"
                      name="Penalties"
                      stroke="#F44336"
                      strokeWidth={2}
                      dot={{ r: 4 }}
                      activeDot={{ r: 6 }}
                    />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Per-Member Deduction Table */}
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                <div className="flex h-7 w-7 items-center justify-center rounded-md bg-[#009A44]/10">
                  <Calculator className="h-3.5 w-3.5 text-[#009A44]" />
                </div>
                <h3 className="text-sm font-semibold">Per-Member Deductions</h3>
              </div>
              <ReportExport
                data={memberDeductions}
                filename="payroll-report"
                title="Payroll Report"
                columns={exportColumns}
              />
            </div>
            {loading ? (
              <div className="space-y-3">
                {[1, 2, 3, 4, 5].map((i) => (
                  <div key={i} className="flex items-center justify-between rounded-lg border p-3">
                    <div className="flex items-center gap-3">
                      <Skeleton className="h-8 w-8 rounded-full" />
                      <div className="space-y-1">
                        <Skeleton className="h-4 w-32" />
                        <Skeleton className="h-3 w-20" />
                      </div>
                    </div>
                    <Skeleton className="h-4 w-20" />
                  </div>
                ))}
              </div>
            ) : memberDeductions.length === 0 ? (
              <div className="py-12 text-center">
                <Calculator className="mx-auto mb-3 h-10 w-10 text-gray-300" />
                <p className="text-sm text-gray-500">No deduction data found</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b text-left">
                      <th className="pb-2 pr-4 font-medium text-gray-500">Member</th>
                      <th className="pb-2 pr-4 font-medium text-gray-500 text-right">Savings</th>
                      <th className="pb-2 pr-4 font-medium text-gray-500 text-right">Loan Repayments</th>
                      <th className="pb-2 pr-4 font-medium text-gray-500 text-right">Penalties</th>
                      <th className="pb-2 pr-4 font-medium text-gray-500 text-right">Total</th>
                      <th className="pb-2 font-medium text-gray-500">HR Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {memberDeductions.map((member) => (
                      <tr
                        key={member.memberId}
                        className="border-b last:border-b-0 hover:bg-gray-50/50"
                      >
                        <td className="py-3 pr-4 font-medium text-gray-900">
                          {member.memberName}
                        </td>
                        <td className="py-3 pr-4 text-right font-semibold text-[#009A44]">
                          {formatCurrency(member.savings)}
                        </td>
                        <td className="py-3 pr-4 text-right font-semibold text-blue-600">
                          {formatCurrency(member.loanRepayments)}
                        </td>
                        <td className="py-3 pr-4 text-right font-semibold text-red-600">
                          {formatCurrency(member.penalties)}
                        </td>
                        <td className="py-3 pr-4 text-right font-bold text-gray-900">
                          {formatCurrency(member.total)}
                        </td>
                        <td className="py-3">
                          <span
                            className={cn(
                              "inline-flex rounded-full px-2 py-0.5 text-xs font-medium capitalize",
                              hrStatusBadge[member.hrStatus] || "bg-gray-100 text-gray-600"
                            )}
                          >
                            {member.hrStatus}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>

        {/* HR-Ready List */}
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                <div className="flex h-7 w-7 items-center justify-center rounded-md bg-[#009A44]/10">
                  <FileCheck className="h-3.5 w-3.5 text-[#009A44]" />
                </div>
                <h3 className="text-sm font-semibold">HR-Ready Members</h3>
              </div>
              <ReportExport
                data={hrReadyList}
                filename="payroll-hr-ready"
                title="HR-Ready Payroll"
                columns={hrReadyColumns}
              />
            </div>
            {loading ? (
              <div className="space-y-3">
                {[1, 2, 3].map((i) => (
                  <div key={i} className="flex items-center justify-between rounded-lg border p-3">
                    <div className="flex items-center gap-3">
                      <Skeleton className="h-8 w-8 rounded-full" />
                      <div className="space-y-1">
                        <Skeleton className="h-4 w-32" />
                        <Skeleton className="h-3 w-20" />
                      </div>
                    </div>
                    <Skeleton className="h-4 w-20" />
                  </div>
                ))}
              </div>
            ) : hrReadyList.length === 0 ? (
              <div className="py-12 text-center">
                <FileCheck className="mx-auto mb-3 h-10 w-10 text-gray-300" />
                <p className="text-sm text-gray-500">No HR-ready members found</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b text-left">
                      <th className="pb-2 pr-4 font-medium text-gray-500">Member</th>
                      <th className="pb-2 pr-4 font-medium text-gray-500 text-right">Total Deduction</th>
                      <th className="pb-2 font-medium text-gray-500">Breakdown</th>
                    </tr>
                  </thead>
                  <tbody>
                    {hrReadyList.map((member) => (
                      <tr
                        key={member.memberId}
                        className="border-b last:border-b-0 hover:bg-gray-50/50"
                      >
                        <td className="py-3 pr-4 font-medium text-gray-900">
                          {member.memberName}
                        </td>
                        <td className="py-3 pr-4 text-right font-bold text-[#009A44]">
                          {formatCurrency(member.total)}
                        </td>
                        <td className="py-3 text-gray-600 text-sm">
                          {member.breakdown}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
